/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  AppProps,
  AppLayoutProps,
  AppInitialProps,
  AppContext,
} from 'next/app';
import type { NextComponentType } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Script from 'next/script';
import classNames from 'classnames';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { Inter } from '@/assets/fonts/inter/local-font';
import { SFArabic } from '@/assets/fonts/sf-arabic/local-font';
import { GulfArabic } from '@/assets/fonts/gulf-arabic/local-font';
import { MeQuranVoltNewmet } from '@/assets/fonts/me-quran-volt-newmet/local-font';
import { appWithTranslation } from 'next-i18next';
import { createEmotionCache } from '@/infrastructure/mui/emotion-cache';
import { Locales } from '@/infrastructure/enum/locales';
import AppThemeProvider from '@/infrastructure/context/theme-context';
import { WorkingEnvironment } from '@/infrastructure/enum/working-environment';
import 'swiper/css';
import '../assets/styles/normalize.scss';
import '../assets/styles/globals.scss';
import AuthUserProvider from '@/infrastructure/context/auth-context';
import BreadcrumbProvider from '@/infrastructure/context/breadcrumb-context';
import PlayerProvider from '@/infrastructure/context/player-context';
import NavbarProvider from '@/infrastructure/context/navbar-context';
import UserProvider from '@/infrastructure/context/user-context';
import PlaylistProvider from '@/infrastructure/context/playlist-context';
import ReadBookmarksProvider from '@/infrastructure/context/read-bookmarks-context';
import AudioBookmarksProvider from '@/infrastructure/context/audio-bookmarks-context';
import ReadSurahSlugsProvider from '@/infrastructure/context/read-surah-slugs-context';
import { isGtmAllowedRoute } from '@/infrastructure/utils/route-policies';
import 'smartbanner.js/dist/smartbanner.css';

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const NODE_ENV = `${process.env.NODE_ENV}`;
const APP_ID = `${process.env.APP_ID}`;
const GOOGLE_PLAY_URL = `${process.env.GOOGLE_PLAY_URL}`;
const IS_STATIC_EXPORT = process.env.NEXT_PUBLIC_STATIC_EXPORT === 'true';

interface IAppComponentProps extends AppProps {
  emotionCache?: EmotionCache;
}

type appProps = IAppComponentProps & AppLayoutProps;

const AppComponent: NextComponentType<
  AppContext,
  AppInitialProps,
  AppLayoutProps
> = (props: appProps) => {
  const router = useRouter();
  const { locale = 'en', locales = [] } = router;
  const [shouldLoadSmartBanner, setShouldLoadSmartBanner] = useState(false);
  const [shouldLoadGtm, setShouldLoadGtm] = useState(false);

  const clientSideEmotionCache = createEmotionCache(locale);
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  const getLayout = Component.getLayout || ((page: React.ReactNode) => page);

  const fontsClassNames = classNames(
    Inter.variable,
    {
      [SFArabic.variable]: locale === Locales.ar,
    },
    GulfArabic.variable,
    MeQuranVoltNewmet.variable
  );
  const canLoadGtm =
    NODE_ENV === WorkingEnvironment.PRODUCTION &&
    !!GTM_ID &&
    isGtmAllowedRoute(router.pathname);

  useEffect(() => {
    document.dir = 'ltr';
    if (locale === Locales.ar) {
      document.dir = 'rtl';
    }
  }, [locale]);

  useEffect(() => {
    if (!IS_STATIC_EXPORT || !router.isReady) {
      return;
    }

    const { pathname, search, hash } = window.location;
    if (pathname === '/' || !pathname.endsWith('/') || pathname.includes('.')) {
      return;
    }

    window.history.replaceState(
      window.history.state,
      '',
      `${pathname.slice(0, -1)}${search}${hash}`
    );
  }, [router.asPath, router.isReady]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const isMobileUA = /Android|iPhone|iPad|iPod/i.test(
      window.navigator.userAgent
    );

    if (!isMobileUA) {
      return;
    }

    const onFirstInteraction = () => {
      setShouldLoadSmartBanner(true);
    };

    window.addEventListener('pointerdown', onFirstInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener('keydown', onFirstInteraction, { once: true });

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction);
      window.removeEventListener('keydown', onFirstInteraction);
    };
  }, []);

  useEffect(() => {
    if (!canLoadGtm || shouldLoadGtm || typeof window === 'undefined') {
      return;
    }

    const onFirstInteraction = () => {
      setShouldLoadGtm(true);
    };

    window.addEventListener('pointerdown', onFirstInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener('keydown', onFirstInteraction, { once: true });
    window.addEventListener('scroll', onFirstInteraction, {
      once: true,
      passive: true,
    });

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction);
      window.removeEventListener('keydown', onFirstInteraction);
      window.removeEventListener('scroll', onFirstInteraction);
    };
  }, [canLoadGtm, shouldLoadGtm]);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <meta
          name="apple-itunes-app"
          content={`app-id=${APP_ID}, app-argument=https://apps.apple.com/us/app/id${APP_ID}`}
        />
        <meta
          name="smartbanner:title"
          content="Quran Pro: Read the Holy Quran"
        />
        <meta name="smartbanner:author" content="Quanticapps Ltd" />
        <meta name="smartbanner:price" content="FREE" />
        <meta
          name="smartbanner:price-suffix-apple"
          content=" - On the App Store"
        />
        <meta
          name="smartbanner:price-suffix-google"
          content=" - In Google Play"
        />
        <meta name="smartbanner:icon-apple" content="/logo/logo.webp" />
        <meta name="smartbanner:icon-google" content="/logo/logo.webp" />
        <meta name="smartbanner:button" content="VIEW" />
        <meta
          name="smartbanner:button-url-apple"
          content={`https://apps.apple.com/us/app/id${APP_ID}`}
        />
        <meta name="smartbanner:button-url-google" content={GOOGLE_PLAY_URL} />
        <meta name="smartbanner:enabled-platforms" content="android,ios" />
        <meta name="smartbanner:custom-design-modifier" content="ios" />
        <meta name="smartbanner:close-label" content="Close" />
        {/* 
          Exclude Safari browsers from showing the smartbanner.
          The regex "^.*(Version).*Safari" matches user agent strings for Safari browsers,
          which typically include both "Version" and "Safari" in their user agent string.
          This is done because we added default apple smartbanner for iOS with Safari browser
        */}
        <meta
          name="smartbanner:exclude-user-agent-regex"
          content="^.*(Version).*Safari"
        />
      </Head>
      {shouldLoadSmartBanner && (
        <Script
          id="smartbanner"
          src="/scripts/smartbanner.min.js"
          strategy="afterInteractive"
          onLoad={() => {
            window.dispatchEvent(new Event('load'));
          }}
        />
      )}
      {canLoadGtm && shouldLoadGtm && (
        <Script
          id="google-tag-manager"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');`,
          }}
        />
      )}
      <PlaylistProvider>
        <AudioBookmarksProvider>
          <ReadBookmarksProvider>
            <UserProvider>
              <NavbarProvider>
                <PlayerProvider>
                  <AppThemeProvider>
                    <AuthUserProvider>
                      <BreadcrumbProvider>
                        <ReadSurahSlugsProvider>
                          <div className={fontsClassNames}>
                            {getLayout(<Component {...pageProps} />)}
                          </div>
                        </ReadSurahSlugsProvider>
                      </BreadcrumbProvider>
                    </AuthUserProvider>
                  </AppThemeProvider>
                </PlayerProvider>
              </NavbarProvider>
            </UserProvider>
          </ReadBookmarksProvider>
        </AudioBookmarksProvider>
      </PlaylistProvider>
    </CacheProvider>
  );
};

export default appWithTranslation(AppComponent);
